import { ErrorResponse } from '@models/error.response';
import { TranslateService } from '@ngx-translate/core';
import { MainComponent } from './../main/main.component';
import { of, from } from 'rxjs';
import { ActionType } from '@type/action.type';
import { AppConstant } from '@constants/app.constant';
import { NgForm } from '@angular/forms';
import { FormValidationUtil } from '@utils/form.validation';
import { RoleService } from '@services/role.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PermissionGroup } from '@models/permission/permission-group';
import { Component, ViewChild } from '@angular/core';
import { PermissionGroupService } from '@services/permission-group.service';
import { Role } from '@models/role';
import { PermissionGroupCategory } from '@models/permission/permission-group';
import swal from 'sweetalert2';
import { switchMap, map, flatMap, filter, toArray, mergeMap, reduce } from 'rxjs/operators';
import { ModulePathConstant } from '@app/constants/modulePath.constant';
import { CommonService } from '@app/services/common.service';

@Component({
    // templateUrl: './role.add.component.html',
    // styleUrls: ['./role.add.component.scss']
    templateUrl: './role-setup.component.html',
    styleUrls: ['./role-setup.component.scss']
})
export class RoleSetupComponent {

    @ViewChild('roleForm', { static: false }) roleForm: NgForm;
    isFieldError = FormValidationUtil.isFieldError;
    action: ActionType;
    loading: boolean = false;
    isShowAdd:boolean = false;
    isShowUpdate:boolean = false;
    permissionGroups: PermissionGroup[] = [];
    role: Role;
    permissionGroupCategories: PermissionGroupCategory[] = ['ADMIN'];

    constructor(
        private permissionGroupService: PermissionGroupService,
        private roleService: RoleService,
        private router: Router,
        private route: ActivatedRoute,
        private commonService: CommonService,
        private mainComponent: MainComponent,
        private translate: TranslateService,
        public appConstant: AppConstant) {
    }

    ngOnInit(): void {
        this.role = new Role();

        this.route.params
            .pipe(
                switchMap((params: Params) => {
                    this.loading = true;
                    if (params['id'] === 'new') {
                        this.action = 'CREATE';
                        this.isShowAdd = this.commonService.showAddButton(this.action,'PERM_ROLE_CREATE');
                    } else {
                        this.action = 'EDIT';
                        this.isShowUpdate = this.commonService.showUpdateButton(this.action,'PERM_ROLE_UPDATE');
                        console.info('show update' + this.isShowUpdate)
                    }
                    return of(params);
                }),
                switchMap((params: Params) => {
                    return this.permissionGroupService.get()
                        .pipe(
                            map(permissionGroups => {
                                this.permissionGroups = permissionGroups;
                                this.permissionGroups.sort((x, y) => x.sequence - y.sequence);
                                this.permissionGroups.forEach(group => {
                                    group.permissionSets = group.permissionSets.sort((x, y) => x.sequence - y.sequence);
                                })
                                return params;
                            })
                        );
                }),
                switchMap((params: Params) => {
                    if (this.action == 'CREATE') {
                        return of(new Role());
                    } else {
                        return this.roleService.get(params['id'])
                    }
                })
            )
            .subscribe((role: Role) => {
                this.role = role;
                from(this.permissionGroups)
                    .pipe(
                        flatMap(group => group.permissionSets)
                    ).forEach(set => {
                        if (role.permissionSets.find(rolePermSet => rolePermSet.permissionSetId === set.permissionSetId) != null) {
                            set._selected = true;
                        }
                    });
                this.loading = false;
            });

    }

    getPermissionGroupsByCategory(category: PermissionGroupCategory) {
        return this.permissionGroups.filter(x => x.category == category);
    }

    isAllPermissionChecked(category: PermissionGroupCategory): boolean {
        return this.getPermissionGroupsByCategory(category).every(pg => {
            return pg.permissionSets.every(ps => ps._selected);
        });
    }

    checkAllPermission(event, category: PermissionGroupCategory): void {
        from(this.getPermissionGroupsByCategory(category))
            .pipe(
                flatMap(group => group.permissionSets)
            ).forEach(set => {
                if (event.target.checked) {
                    set._selected = true;
                } else {
                    set._selected = false;
                }
            });
    }

    updateRoleName(value) {
        let roleName = value;

        this.role.roleName.i18nStrings.forEach((item, idx, arr) => {
            arr[idx].value = roleName;
        });
    }

    save(): void {

        //check form for just clicking save without any input
        for (let key of Object.keys(this.roleForm.controls)) {
            this.roleForm.controls[key].markAsDirty();
        }

        from(this.permissionGroups)
            .pipe(
                flatMap(group => group.permissionSets),
                filter(permSet => permSet._selected),
                toArray()
            )
            .subscribe(x => this.role.permissionSets = x);

        if (this.roleForm.valid) {

            switch (this.action) {
                case 'CREATE':
                    this.create();
                    break
                case 'EDIT':
                    this.update();
                    break;
            }
        }
    }

    private create(): void {

        this.mainComponent.enableLoadingMask();
        this.loading = true;

        this.roleService.insert(this.role)
            .subscribe(() => {
                this.mainComponent.disableLoadingMask();
                this.loading = false;

                this.router.navigate([ModulePathConstant.ROLE_PATH]);

                this.translate.get(ModulePathConstant.ROLE_MODULE)
                    .pipe(
                        switchMap(typeStr => this.translate.get(AppConstant.HAS_BEEN_ADDED, { type: typeStr }))
                    )
                    .subscribe(res => {
                        swal.fire({
                            type: 'success',
                            title: res
                        });
                    });
            },
                (error: ErrorResponse) => {
                    this.mainComponent.disableLoadingMask();
                    this.loading = false;
                    from(error.errors)
                        .pipe(
                            mergeMap(error => this.translate.get(error.errorCode)),
                            reduce((acc, value) => acc + value + '<br/>', '')
                        )
                        .subscribe(msg => {

                            this.translate.get(ModulePathConstant.ROLE_MODULE)
                                .pipe(
                                    switchMap(typeStr => this.translate.get(AppConstant.FAIL_TO_ADD, { type: typeStr }))
                                )
                                .subscribe(title => {
                                    swal.fire({
                                        type: 'error',
                                        title: title,
                                        html: msg
                                    });
                                });

                        });
                });
    }

    private update(): void {

        this.mainComponent.enableLoadingMask();
        this.loading = true;

        this.roleService.update(this.role)
            .subscribe(role => {
                this.role = role;
                this.mainComponent.disableLoadingMask();
                this.loading = false;

                this.router.navigate([ModulePathConstant.ROLE_PATH]);

                this.translate.get(ModulePathConstant.ROLE_MODULE)
                    .pipe(
                        switchMap(typeStr => this.translate.get(AppConstant.HAS_BEEN_UPDATED, { type: typeStr }))
                    )
                    .subscribe(res => {
                        swal.fire({
                            type: 'success',
                            title: res
                        });
                    });

            },
                (error: ErrorResponse) => {
                    this.mainComponent.disableLoadingMask();
                    this.loading = false;


                    from(error.errors)
                        .pipe(
                            mergeMap(error => this.translate.get(error.errorCode)),
                            reduce((acc, value) => acc + value + '<br/>', '')
                        )
                        .subscribe(msg => {

                            this.translate.get(ModulePathConstant.ROLE_MODULE)
                                .pipe(
                                    switchMap(typeStr => this.translate.get(AppConstant.FAIL_TO_UPDATE, { type: typeStr }))
                                )
                                .subscribe(title => {
                                    swal.fire({
                                        type: 'error',
                                        title: title,
                                        html: msg
                                    });
                                });
                        });
                })

    }

    cancel(): void {
        this.router.navigate([ModulePathConstant.ROLE_PATH]);
    }

}
